/**
 * post-V9 Section 0 — Iteration 2: typed-array compact entity representation.
 *
 * Proof-of-concept for **LINE** primitive only. Hypothesis E (Iter 1 verdict):
 * the 50.7× heap-to-JSON amplification observed at the synthetic LINE/CIRCLE
 * level is dominated by V8 hidden-class overhead per entity object — each
 * optional field (layer/color/linetype/lineweight/transparency/thickness/...)
 * carries its own slot whether populated or not. Replacing the per-entity
 * record with parallel typed-array buffers (`Float32Array`, `Uint16Array`,
 * `Uint8Array`) eliminates that overhead at the cost of indirection on read.
 *
 * **Scope (proof-of-concept):**
 *  - LineEntity ONLY (4 endpoint floats + style fields)
 *  - Side-by-side measurement only — does NOT replace `__entitiesCache`,
 *    does NOT integrate with the renderer / reducers / worker.
 *  - Existing `LineEntity` shape preserved via lazy reconstruction
 *    (`lineFromCompact(store, index)`) so a future migration can wrap a
 *    `Proxy`/getter facade over the buffers without touching consumers.
 *
 * **Memory model per LINE:**
 *  - 4 × Float32 = 16 B coordinates (start.x, start.y, end.x, end.y)
 *  - 1 × Float32 =  4 B thickness
 *  - 5 × Uint16 = 10 B style table indices (layer, color, linetype,
 *                       lineweight, transparency)
 *  - 1 × Uint8  =  1 B array-mode tag (0=none, 1=rect, 2=polar, 3=path)
 *  - id + spaceId via two parallel string arrays (UTF-16 in V8, ~24 B per
 *    short string — main residual heap cost, but unavoidable since each
 *    line has a unique handle id)
 *  - Optional arrayGroupId / arraySourceOfGroup via sparse Maps
 *    (rare fields → don't pay slot cost on every entity)
 *
 * Result per LINE: ~31 B fixed + id/spaceId. Compare to `LineEntity`
 * record measured in Iter 1 at ~9.6 KB/entity heap retention.
 *
 * **Lookup contract:** the store exposes
 *   - `count` — total LINE entities
 *   - `lineFromCompact(store, index)` — reconstruct full LineEntity
 *   - `setLine(store, index, entity)` — write a single line back
 *   - `internString(store, value)` — get/create a string-table id
 * The string table is shared by all string-typed columns to keep the
 * common case (`layer="0"`, `color="ByLayer"`) at <10 unique strings.
 *
 * **Future extensions (Iter 3+) — out of scope here:**
 *  - Same pattern for the remaining 13 primitive types; each gets its own
 *    typed-array set with type-specific column layout.
 *  - Replace `__entitiesCache` with a per-type compact store + index map.
 *  - Renderer reads typed arrays directly, skipping reconstruction.
 *
 * Iteration 2 measurement target: <1 KB/entity heap (i.e. ≥10× reduction
 * vs. Iter 1's ~9.6 KB/entity).
 */

import { coerceColor, deserializeColor, serializeColor } from "@dxf-ai-editor/drawing-model";
import type { LineEntity } from "@dxf-ai-editor/drawing-model";

import {
  applyBaseMetadata,
  storeBaseMetadata,
  type BaseMetadata,
} from "./compactStores/compactStoreBase";

const FLOATS_PER_LINE = 5; // start.x, start.y, end.x, end.y, thickness
const STYLE_FIELDS_PER_LINE = 5; // layer, color, linetype, lineweight, transparency

/**
 * Initial typed-array capacity. Grows by 2× when exceeded. Chosen to fit
 * synthetic 10k-entity test fixtures without resizing while keeping cold-
 * start cost near zero.
 */
const DEFAULT_CAPACITY = 1024;

/**
 * Mapping of `arrayMode` values to / from the 1-byte tag column.
 * 0 = absent (no arrayMode field), 1=rect, 2=polar, 3=path.
 */
const ARRAY_MODE_VALUES = ["", "rect", "polar", "path"] as const;
type ArrayModeTag = 0 | 1 | 2 | 3;

function arrayModeToTag(mode: LineEntity["arrayMode"]): ArrayModeTag {
  if (mode === "rect") return 1;
  if (mode === "polar") return 2;
  if (mode === "path") return 3;
  return 0;
}

function tagToArrayMode(tag: number): LineEntity["arrayMode"] | undefined {
  if (tag <= 0 || tag >= ARRAY_MODE_VALUES.length) return undefined;
  return ARRAY_MODE_VALUES[tag] as LineEntity["arrayMode"];
}

/**
 * Compact typed-array storage for a homogeneous batch of LINE entities.
 *
 * All buffers are parallel — `index i` refers to the same line across
 * every column. The buffer indices are dense (no holes); deletions
 * compact by swapping with the last element.
 */
export interface CompactLineStore {
  /** Number of LINE entities currently stored. */
  count: number;
  /** Allocated capacity (always ≥ count). Buffers grow 2× when count → capacity. */
  capacity: number;

  /**
   * Endpoint coordinates + thickness, stride = 5:
   *   floats[i*5+0] = start.x
   *   floats[i*5+1] = start.y
   *   floats[i*5+2] = end.x
   *   floats[i*5+3] = end.y
   *   floats[i*5+4] = thickness
   */
  floats: Float32Array;

  /**
   * Style-table indices, stride = 5:
   *   styles[i*5+0] = layer
   *   styles[i*5+1] = color
   *   styles[i*5+2] = linetype
   *   styles[i*5+3] = lineweight
   *   styles[i*5+4] = transparency
   *
   * Each value is an index into `stringTable`. Uint16 caps unique strings
   * at 65,536 — sufficient for any real-world drawing (AutoCAD itself
   * caps layer count at 32,767).
   */
  styles: Uint16Array;

  /** arrayMode tag, 1 byte each (0=absent, 1=rect, 2=polar, 3=path). */
  arrayModes: Uint8Array;

  /** id strings (one per index). */
  ids: string[];
  /** spaceId strings (one per index). */
  spaceIds: string[];

  /**
   * Sparse storage for optional fields that are usually absent. Storing
   * them in Maps means lines without these fields pay zero per-entity
   * cost. Most synthetic fixtures and most real-world drawings have <1%
   * of lines participating in arrays.
   */
  arrayGroupIds: Map<number, string>;
  arraySourceOfGroups: Map<number, string>;

  /**
   * Sparse common-metadata remainder — every `DrawingEntityBase` field with no
   * column above (`ownerHandle` 330, `extensionDictionaryHandle`,
   * `materialHandle` 347, `plotStyleHandle` 390, `visibility` 60, `shadowMode`
   * 284, `linetypeScale` 48, `extraGroups`), captured and re-attached by the
   * SHARED helpers in `compactStores/compactStoreBase.ts`.
   *
   * LINE keeps its own frozen Iter-2 columns and string table, but the
   * remainder is one truth with every other store's: this file calls
   * `storeBaseMetadata` / `applyBaseMetadata` rather than re-deriving the key
   * set, so LINE — by far the most common entity in a real drawing, and the
   * one whose streamed rebuild was dropping all eight groups — cannot drift
   * from the shared base.
   */
  metadata: Map<number, BaseMetadata>;

  /** Shared string table. Index 0 reserved for empty string. */
  stringTable: string[];
  /** Reverse lookup for `internString`. */
  stringIndex: Map<string, number>;

  /** post-V9 Section 0 Iter 6.C — tombstone bitmap. 0 = live, 1 = tombstoned. */
  tombstones: Uint8Array;
  /** post-V9 Section 0 Iter 6.C — running count of tombstoned slots. */
  tombstoneCount: number;
}

export function createCompactLineStore(
  initialCapacity = DEFAULT_CAPACITY,
): CompactLineStore {
  const cap = Math.max(1, initialCapacity);
  const store: CompactLineStore = {
    count: 0,
    capacity: cap,
    floats: new Float32Array(cap * FLOATS_PER_LINE),
    styles: new Uint16Array(cap * STYLE_FIELDS_PER_LINE),
    arrayModes: new Uint8Array(cap),
    ids: new Array<string>(cap).fill(""),
    spaceIds: new Array<string>(cap).fill(""),
    arrayGroupIds: new Map(),
    arraySourceOfGroups: new Map(),
    metadata: new Map(),
    stringTable: [""],
    stringIndex: new Map([["", 0]]),
    tombstones: new Uint8Array(cap),
    tombstoneCount: 0,
  };
  return store;
}

/**
 * Get-or-create a string-table id. Returns the existing index if `value`
 * was already interned, otherwise appends and returns the new index.
 */
export function internString(store: CompactLineStore, value: string): number {
  const existing = store.stringIndex.get(value);
  if (existing !== undefined) return existing;
  const id = store.stringTable.length;
  if (id >= 0xffff) {
    throw new Error(
      `compactEntityStore: string table overflow (>65,535 entries). ` +
        `Switch to Uint32Array indices or split the store.`,
    );
  }
  store.stringTable.push(value);
  store.stringIndex.set(value, id);
  return id;
}

/**
 * Resolve a string-table index back to the original string. Returns ""
 * for index 0 (the reserved empty-string slot).
 */
export function lookupString(store: CompactLineStore, id: number): string {
  return store.stringTable[id] ?? "";
}

function ensureCapacity(store: CompactLineStore, needed: number): void {
  if (needed <= store.capacity) return;
  let newCap = store.capacity;
  while (newCap < needed) newCap *= 2;
  const newFloats = new Float32Array(newCap * FLOATS_PER_LINE);
  newFloats.set(store.floats);
  const newStyles = new Uint16Array(newCap * STYLE_FIELDS_PER_LINE);
  newStyles.set(store.styles);
  const newArrayModes = new Uint8Array(newCap);
  newArrayModes.set(store.arrayModes);
  const newTombstones = new Uint8Array(newCap);
  newTombstones.set(store.tombstones);
  // Grow ids/spaceIds — Array#length write extends with `undefined`; we
  // fill those slots with "" so subsequent `setLine` doesn't write into
  // a sparse hole (V8 deoptimizes mixed-shape arrays).
  const oldLen = store.ids.length;
  store.ids.length = newCap;
  store.spaceIds.length = newCap;
  for (let i = oldLen; i < newCap; i++) {
    store.ids[i] = "";
    store.spaceIds[i] = "";
  }
  store.floats = newFloats;
  store.styles = newStyles;
  store.arrayModes = newArrayModes;
  store.tombstones = newTombstones;
  store.capacity = newCap;
}

/**
 * Append a LINE to the store. Returns the index it was written at.
 * Use `setLine` to overwrite an existing index.
 */
export function appendLine(
  store: CompactLineStore,
  entity: LineEntity,
): number {
  const index = store.count;
  ensureCapacity(store, index + 1);
  store.count++;
  setLine(store, index, entity);
  return index;
}

/**
 * Write a LINE at `index`. Caller is responsible for bounds (`index < count`).
 */
export function setLine(
  store: CompactLineStore,
  index: number,
  entity: LineEntity,
): void {
  const f = index * FLOATS_PER_LINE;
  store.floats[f + 0] = entity.start.x;
  store.floats[f + 1] = entity.start.y;
  store.floats[f + 2] = entity.end.x;
  store.floats[f + 3] = entity.end.y;
  store.floats[f + 4] = entity.thickness;

  const s = index * STYLE_FIELDS_PER_LINE;
  store.styles[s + 0] = internString(store, entity.layer);
  // EPIC-COLOR-UNION S1 — intern the LOSSLESS serialized colour. `serializeColor`
  // keeps the shared string table byte-identical for the common cases (ByLayer /
  // ByBlock / index / plain rgb) and uses a `!EC:`-prefixed payload for the
  // true-/book-colours whose 62/430 siblings the legacy string would drop.
  // `coerceColor` first defends raw entity literals (tests / reducers) that
  // bypassed the schema and carry a legacy-string or absent `color`. Rehydrated
  // losslessly in `lineFromCompact` via `deserializeColor`.
  store.styles[s + 1] = internString(
    store,
    serializeColor(coerceColor(entity.color)),
  );
  store.styles[s + 2] = internString(store, entity.linetype);
  store.styles[s + 3] = internString(store, entity.lineweight);
  store.styles[s + 4] = internString(store, entity.transparency);

  store.arrayModes[index] = arrayModeToTag(entity.arrayMode);
  store.ids[index] = entity.id;
  store.spaceIds[index] = entity.spaceId;

  if (entity.arrayGroupId !== undefined) {
    store.arrayGroupIds.set(index, entity.arrayGroupId);
  } else {
    store.arrayGroupIds.delete(index);
  }
  if (entity.arraySourceOfGroup !== undefined) {
    store.arraySourceOfGroups.set(index, entity.arraySourceOfGroup);
  } else {
    store.arraySourceOfGroups.delete(index);
  }
  // The rest of the base model, as a remainder rather than an enumerated
  // list — see `CompactLineStore.metadata`.
  storeBaseMetadata(store.metadata, index, entity);
  // post-V9 Section 0 Iter 6.C — fresh writes are always live.
  store.tombstones[index] = 0;
}

/** post-V9 Section 0 Iter 6.C — true if line slot at `index` is tombstoned. */
export function isLineTombstoned(
  store: CompactLineStore,
  index: number,
): boolean {
  if (index < 0 || index >= store.capacity) return false;
  return store.tombstones[index] === 1;
}

/**
 * post-V9 Section 0 Iter 6.C — mark line slot `index` as tombstoned. Idempotent.
 * Returns true if the call transitioned 0→1; false otherwise.
 */
export function markLineTombstone(
  store: CompactLineStore,
  index: number,
): boolean {
  if (index < 0 || index >= store.capacity) return false;
  if (store.tombstones[index] === 1) return false;
  store.tombstones[index] = 1;
  store.tombstoneCount += 1;
  return true;
}

/**
 * Reconstruct a `LineEntity` from compact storage. The return value is a
 * fresh object on each call — callers must not assume identity.
 *
 * This is the "exit ramp" for the proof-of-concept: any consumer that
 * needs the schema-shaped record can call this to get an equivalent
 * `LineEntity` without `__entitiesCache` having to retain the full
 * record.
 */
export function lineFromCompact(
  store: CompactLineStore,
  index: number,
): LineEntity {
  if (index < 0 || index >= store.count) {
    throw new RangeError(
      `lineFromCompact: index ${index} out of range (count=${store.count})`,
    );
  }
  const f = index * FLOATS_PER_LINE;
  const s = index * STYLE_FIELDS_PER_LINE;
  const mode = tagToArrayMode(store.arrayModes[index]!);
  const result: LineEntity = {
    type: "Line",
    id: store.ids[index]!,
    spaceId: store.spaceIds[index]!,
    layer: lookupString(store, store.styles[s + 0]!),
    // EPIC-COLOR-UNION S1 — rehydrate the interned colour LOSSLESSLY into the
    // typed union (`!EC:` rich payload parsed back verbatim; plain legacy string
    // routed through coerceColor). Faithful round-trip of writeLine's
    // serializeColor.
    color: deserializeColor(lookupString(store, store.styles[s + 1]!)),
    linetype: lookupString(store, store.styles[s + 2]!),
    lineweight: lookupString(store, store.styles[s + 3]!),
    transparency: lookupString(store, store.styles[s + 4]!),
    start: { x: store.floats[f + 0]!, y: store.floats[f + 1]! },
    end: { x: store.floats[f + 2]!, y: store.floats[f + 3]! },
    thickness: store.floats[f + 4]!,
  };
  if (mode !== undefined) result.arrayMode = mode;
  const grp = store.arrayGroupIds.get(index);
  if (grp !== undefined) result.arrayGroupId = grp;
  const src = store.arraySourceOfGroups.get(index);
  if (src !== undefined) result.arraySourceOfGroup = src;
  applyBaseMetadata(result, store.metadata.get(index));
  return result;
}

/**
 * Bulk-build a compact store from an array of `LineEntity` records.
 * Accepts a generic `unknown[]` and filters to LINE entities so it can
 * be called against the heterogeneous `__entitiesCache` arrays without
 * pre-filtering at the call site.
 */
export function compactStoreFromEntities(
  entities: ReadonlyArray<unknown>,
): CompactLineStore {
  const lineCount = countLines(entities);
  const store = createCompactLineStore(Math.max(DEFAULT_CAPACITY, lineCount));
  for (const e of entities) {
    if (isLineEntity(e)) appendLine(store, e);
  }
  return store;
}

function isLineEntity(e: unknown): e is LineEntity {
  return (
    typeof e === "object" &&
    e !== null &&
    (e as { type?: unknown }).type === "Line"
  );
}

function countLines(entities: ReadonlyArray<unknown>): number {
  let n = 0;
  for (const e of entities) if (isLineEntity(e)) n++;
  return n;
}

/**
 * Approximate fixed-cost heap retention per LINE in a compact store.
 * Ignores the variable-length id / spaceId strings (V8 represents short
 * latin-1 strings at ~16 B header + 2 B per char); those dominate at
 * very high counts and should be reduced via id-sharing or numeric
 * handles in a future iteration.
 *
 * Useful for sanity-checking the side-by-side measurement and for unit
 * tests that assert a lower bound on the reduction.
 */
export function estimateCompactBytesPerLine(): number {
  const floatBytes = FLOATS_PER_LINE * 4;
  const styleBytes = STYLE_FIELDS_PER_LINE * 2;
  const tagBytes = 1;
  // 2 string slots in the parallel arrays — V8 stores pointers (8 B on
  // 64-bit), the actual string body is shared with the Map keys / DXF
  // bridge so we don't double-count it as "compact overhead".
  const stringPtrBytes = 2 * 8;
  return floatBytes + styleBytes + tagBytes + stringPtrBytes;
}
